/**
 * CompanyDetailPage — refonte 9.P.2.R1 UX/layout revamp.
 *
 * Pattern UX commun (réutilisable R2/R3) :
 *   - Sticky page header (top-16 sous AppShell topbar) avec breadcrumb +
 *     H1 + status badge + actions Archive/Edit toujours visibles
 *   - 2-col layout desktop `lg:grid-cols-3` : main 2/3 (sections données)
 *     + side panel 1/3 (PrimaryBroker + quick stats)
 *   - <CompanyCardSection> moderne pour chaque domaine (header border-b + body)
 *   - <CompanyStatusBadge> visualisation cohérente
 *   - Mobile : stack vertical avec side items intercalés
 *
 * Logique métier strictement préservée : `useCompany`, `useArchiveCompany`,
 * `useContacts`/`useCreateContact`, `PrimaryBrokerEditor` (incl. caveat 8.C),
 * `confirm()` au archive, ApiException + getApiErrorMessage bilingue.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { BlocRepliable } from "../../components/BlocRepliable";
import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { useArchiveCompany, useCompany, useRestoreCompany } from "../../lib/queries/companies";
import { useContacts, useCreateContact } from "../../lib/queries/contacts";
import { toast } from "../../lib/toast";

import { CompanyActions } from "./_components/CompanyActions";
import { CompanyCardSection } from "./_components/CompanyCardSection";
import { CompanyPressingSection } from "./_components/CompanyPressingSection";
import { CompanyProjectsSection } from "./_components/CompanyProjectsSection";
import { CompanyStatusBadge } from "./_components/CompanyStatusBadge";
import { PrimaryBrokerEditor } from "./_components/PrimaryBrokerEditor";

export function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const navigate = useNavigate();

  const company = useCompany(id);
  const archive = useArchiveCompany();
  const restore = useRestoreCompany(id ?? "");

  if (company.isLoading) {
    return (
      <div className="mx-auto w-full max-w-screen-xl">
        <DetailSkeleton />
      </div>
    );
  }

  if (company.error) {
    return (
      <div className="mx-auto w-full max-w-screen-xl">
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          {company.error instanceof ApiException
            ? getApiErrorMessage(company.error, lang) || t("errors.generic")
            : t("errors.generic")}
        </div>
      </div>
    );
  }

  const c = company.data;
  if (!c) return null;

  const onArchive = async () => {
    if (!confirm(t("companies.actions.archive_confirm"))) return;
    try {
      await archive.mutateAsync(c.id);
      navigate("/companies");
    } catch {
      /* error visible dans archive.error */
    }
  };

  const onRestore = async () => {
    try {
      await restore.mutateAsync();
      toast.success(t("companies.actions.restore_success", { name: c.legal_name }));
      // Refetch déjà déclenché par useRestoreCompany.onSuccess. La row redevient
      // active → CompanyStatusBadge mis à jour + boutons Archive/Edit réapparaissent.
    } catch (err) {
      const msg =
        err instanceof ApiException
          ? getApiErrorMessage(err, lang) || t("companies.actions.restore_failed")
          : t("companies.actions.restore_failed");
      toast.error(msg);
    }
  };

  return (
    <div className="mx-auto w-full max-w-screen-xl">
      {/* Sticky header — `top-16` mobile / `lg:top-0` desktop (main scroll-container 9.P.2.R2) */}
      <header className="sticky top-16 z-10 -mx-4 border-b border-slate-200 bg-slate-50/95 px-4 py-4 backdrop-blur-sm lg:top-0 lg:-mx-8 lg:px-8 lg:py-5">
        <nav aria-label="breadcrumb" className="mb-2 text-sm">
          <Link
            to="/companies"
            className="focus-visible:ring-brand-500 rounded text-slate-600 hover:text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            ← {t("companies.title")}
          </Link>
        </nav>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">{c.legal_name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <CompanyStatusBadge status={c.status} pulseActive />
              {c.trade_name && c.trade_name !== c.legal_name && (
                <span className="text-sm text-slate-600">{c.trade_name}</span>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {c.status === "active" ? (
              <>
                <button
                  type="button"
                  onClick={onArchive}
                  disabled={archive.isPending}
                  className="focus-visible:ring-brand-500 touch-manipulation rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 motion-reduce:transition-none"
                >
                  {archive.isPending ? t("app.loading") : t("companies.actions.archive")}
                </button>
                <Link
                  to={`/companies/${c.id}/edit`}
                  className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 motion-reduce:transition-none"
                >
                  {t("common.edit")}
                </Link>
              </>
            ) : (
              // Archived = read-only — Restore est l'action principale.
              // PATCH /:id { status: 'active' } via useRestoreCompany (9.P.2.R2
              // post-smoke). Pas d'Edit ni Archive : il faut d'abord restaurer.
              <button
                type="button"
                onClick={onRestore}
                disabled={restore.isPending}
                className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 motion-reduce:transition-none"
              >
                {restore.isPending ? t("app.loading") : t("companies.actions.restore")}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* 9.BF.1 — l'entreprise ORIENTE. Ce qui appelle une décision vient en
          premier ; l'identité administrative, qui ne change pas d'une visite à
          l'autre, est repliée sous les blocs de travail. */}
      <div className="mt-6 space-y-6">
        <CompanyActions companyId={c.id} />

        <CompanyPressingSection companyId={c.id} />

        <CompanyProjectsSection companyId={c.id} />

        <div data-guide-target="client" id="contacts">
          <ContactsSection companyId={c.id} />
        </div>

        <CompanyCardSection title={t("companies.primary_broker.title")}>
          <PrimaryBrokerEditor companyId={c.id} currentBrokerId={c.primary_broker_id} />
        </CompanyCardSection>

        {/* Les détails administratifs, ensemble et repliés : les traverser à
            chaque visite pour atteindre les dossiers était le coût principal de
            l'ancienne fiche. */}
        <BlocRepliable titre={t("company_hub.details.title")}>
          <div className="space-y-6">
            <CompanyCardSection title={t("companies.create.section.identity")}>
              <FieldGrid>
                <Field
                  label={t("companies.fields.identification_type")}
                  value={
                    c.identification_type
                      ? t(`companies.fields.identification_type_options.${c.identification_type}`)
                      : null
                  }
                />
                <Field label={t("companies.fields.business_number")} value={c.business_number} />
                <Field label={t("companies.fields.industry")} value={c.industry} />
                <Field label={t("companies.fields.founded_year")} value={c.founded_year} />
                <Field
                  label={t("companies.fields.language")}
                  value={c.language === "fr" ? "Français" : "English"}
                />
                <Field label={t("companies.fields.employee_count")} value={c.employee_count} />
              </FieldGrid>
            </CompanyCardSection>

            <CompanyCardSection title={t("companies.create.section.address")}>
              <FieldGrid>
                <Field label={t("companies.fields.address_street")} value={c.address_street} />
                <Field label={t("companies.fields.address_city")} value={c.address_city} />
                <Field label={t("companies.fields.address_province")} value={c.address_province} />
                <Field label={t("companies.fields.address_postal")} value={c.address_postal} />
                <Field label={t("companies.fields.address_country")} value={c.address_country} />
              </FieldGrid>
            </CompanyCardSection>

            <CompanyCardSection title={t("companies.create.section.contact")}>
              <FieldGrid>
                <Field label={t("companies.fields.phone")} value={c.phone} />
                <Field label={t("companies.fields.fax")} value={c.fax} />
                <Field label={t("companies.fields.email")} value={c.email} />
                <Field label={t("companies.fields.website")} value={c.website} />
                <Field label={t("companies.fields.plan_admin_name")} value={c.plan_admin_name} />
                <Field label={t("companies.fields.plan_admin_email")} value={c.plan_admin_email} />
              </FieldGrid>
            </CompanyCardSection>

            <CompanyCardSection title={t("companies.create.section.workforce")}>
              <FieldGrid>
                <BoolField label={t("companies.fields.has_seasonal")} value={c.has_seasonal} />
                <BoolField label={t("companies.fields.has_commission")} value={c.has_commission} />
                <BoolField label={t("companies.fields.has_unionized")} value={c.has_unionized} />
                <BoolField
                  label={t("companies.fields.has_remote_abroad")}
                  value={c.has_remote_abroad}
                />
                <BoolField
                  label={t("companies.fields.considers_remote_abroad")}
                  value={c.considers_remote_abroad}
                />
                <BoolField label={t("companies.fields.family_ties")} value={c.family_ties} />
                {c.family_ties && c.family_ties_note && (
                  <Field
                    label={t("companies.fields.family_ties_note")}
                    value={c.family_ties_note}
                  />
                )}
              </FieldGrid>
            </CompanyCardSection>

            {c.has_current_plan && (
              <CompanyCardSection title={t("companies.create.section.current_plan")}>
                <FieldGrid>
                  <Field label={t("companies.fields.current_insurer")} value={c.current_insurer} />
                  <Field
                    label={t("companies.fields.current_policy_number")}
                    value={c.current_policy_number}
                  />
                  <Field
                    label={t("companies.fields.current_since_date")}
                    value={c.current_since_date}
                  />
                  <Field
                    label={t("companies.fields.current_renewal_date")}
                    value={c.current_renewal_date}
                  />
                </FieldGrid>
              </CompanyCardSection>
            )}

            {c.notes && (
              <CompanyCardSection title={t("companies.create.section.notes")}>
                <p className="whitespace-pre-wrap text-sm text-slate-700">{c.notes}</p>
              </CompanyCardSection>
            )}
          </div>
        </BlocRepliable>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sous-composants helpers
// ---------------------------------------------------------------------------

function FieldGrid({ children }: { children: React.ReactNode }) {
  return <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">{children}</dl>;
}

function Field({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div>
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-900">
        {value !== null && value !== undefined && value !== "" ? (
          value
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </dd>
    </div>
  );
}

function BoolField({ label, value }: { label: string; value: boolean | null | undefined }) {
  const { t } = useTranslation();
  const display = value === true ? t("common.yes") : value === false ? t("common.no") : "—";
  return (
    <div>
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd
        className={
          value === null || value === undefined
            ? "mt-0.5 text-sm text-slate-400"
            : "mt-0.5 text-sm text-slate-900"
        }
      >
        {display}
      </dd>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-32 rounded-xl bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-48 rounded-xl bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none"
            />
          ))}
        </div>
        <div className="lg:col-span-1">
          <div className="h-48 rounded-xl bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ContactsSection — liste contacts + ajout inline (logique préservée)
// ---------------------------------------------------------------------------

function ContactsSection({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const contacts = useContacts(companyId, { limit: 20 });
  const create = useCreateContact(companyId);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    title: "",
    email: "",
    phone: "",
    is_primary: false,
  });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.full_name.trim()) return;
    try {
      await create.mutateAsync({
        full_name: form.full_name.trim(),
        title: form.title.trim() || undefined,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        is_primary: form.is_primary,
      });
      setForm({ full_name: "", title: "", email: "", phone: "", is_primary: false });
      setOpen(false);
    } catch {
      /* error visible via create.error */
    }
  };

  return (
    <CompanyCardSection
      title={t("companies.contacts.title")}
      headerAction={
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="text-brand-700 hover:bg-brand-50 focus-visible:ring-brand-500 touch-manipulation rounded-md px-2.5 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          {open ? t("common.cancel") : `+ ${t("companies.contacts.add")}`}
        </button>
      }
    >
      {open && (
        <form
          onSubmit={onSubmit}
          className="mb-4 grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 sm:grid-cols-2"
        >
          <label className="block sm:col-span-2">
            <span className="label">{t("companies.contacts.fields.full_name")} *</span>
            <input
              className="input"
              value={form.full_name}
              onChange={(e) => setForm((s) => ({ ...s, full_name: e.target.value }))}
              required
            />
          </label>
          <label className="block">
            <span className="label">{t("companies.contacts.fields.title")}</span>
            <input
              className="input"
              value={form.title}
              onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
            />
          </label>
          <label className="block">
            <span className="label">{t("companies.contacts.fields.email")}</span>
            <input
              type="email"
              className="input"
              value={form.email}
              onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
            />
          </label>
          <label className="block">
            <span className="label">{t("companies.contacts.fields.phone")}</span>
            <input
              className="input"
              value={form.phone}
              onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
            />
          </label>
          <label className="flex items-center gap-2 sm:col-span-2">
            <input
              type="checkbox"
              checked={form.is_primary}
              onChange={(e) => setForm((s) => ({ ...s, is_primary: e.target.checked }))}
              className="text-brand-600 focus:ring-brand-500 h-4 w-4 rounded border-slate-300"
            />
            <span className="text-sm text-slate-700">
              {t("companies.contacts.fields.is_primary")}
            </span>
          </label>
          <div className="flex justify-end sm:col-span-2">
            <button type="submit" className="btn-primary" disabled={create.isPending}>
              {create.isPending ? t("app.loading") : t("common.save")}
            </button>
          </div>
        </form>
      )}

      {contacts.isLoading && !contacts.data ? (
        <p className="text-sm text-slate-400">{t("app.loading")}</p>
      ) : contacts.data && contacts.data.items.length === 0 ? (
        <p className="text-sm text-slate-500">{t("companies.contacts.empty")}</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {contacts.data?.items.map((ct) => (
            <li key={ct.id} className="py-2.5">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-900">
                    {ct.full_name}
                    {ct.is_primary && (
                      <span className="bg-brand-100 text-brand-700 inline-flex rounded-full px-2 py-0.5 text-xs font-medium">
                        {t("companies.contacts.primary")}
                      </span>
                    )}
                  </div>
                  {ct.title && <div className="text-xs text-slate-500">{ct.title}</div>}
                </div>
                <div className="text-right text-xs text-slate-500">
                  {ct.email && <div>{ct.email}</div>}
                  {ct.phone && <div>{ct.phone}</div>}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </CompanyCardSection>
  );
}
